import { CITY } from '@city'
import { createFavoritesService, createSearchService } from '@metro/suggester'
import type {
  FavoritesService,
  LocationPermission,
  NearbyResolution,
  SearchService,
  SuggesterStation,
} from '@metro/suggester'
import type { SchemaData, SchemaStation } from '../schema/types'
import { appLocation, isInsideHomeRegion } from './homeRegion'
import { haversineMeters } from './nearestStationProvider'

/**
 * Host-side Suggester service wiring (TASK-318 re-skin).
 *
 * `@metro/suggester` is domain-agnostic — it receives a
 * {@link SearchService}, a {@link FavoritesService}, a Popular list and a
 * nearby resolver from the host. This module builds those.
 *
 * No real Metro search backend exists yet, so the search service is
 * **bundled-only**: every query resolves against the offline station
 * catalogue. The abstraction (spec §13) is in place so a real online
 * provider can be slotted in without touching the Suggester or this
 * module's consumers.
 */

/**
 * The shared favourites store (spec §16) — a module-level singleton so
 * the same instance is read by Suggester and written by
 * StationDetails's favourite toggle, and so favourites survive an
 * overlay close / reopen within a session.
 */
export const favoritesService: FavoritesService = createFavoritesService()

/**
 * Build the Suggester search service for a given station catalogue.
 *
 * Bundled-only today (no `online` provider) — `createSearchService`
 * resolves every query against `catalogue` and reports `offline: false`
 * (there is no backend to fail). When a Metro search endpoint lands,
 * pass an `online` provider here and the offline fallback + the
 * `offline` Suggester state light up automatically.
 */
export function buildSearchService(catalogue: readonly SuggesterStation[]): SearchService {
  return createSearchService({ offlineData: catalogue })
}

/**
 * Pick the Popular fallback list — the 8 stations shown in `idle` when
 * Nearby + Favorites + Recents are all empty (spec §3.4).
 *
 * Backend-provided in the contract; stubbed here as the first 8 of the
 * catalogue until a Popular endpoint exists. Deterministic so the idle
 * fallback is stable across reloads.
 */
export function buildPopular(catalogue: readonly SuggesterStation[]): readonly SuggesterStation[] {
  return catalogue.slice(0, 8)
}

/**
 * Max nearby stations resolved from one fix. Cross-platform contract with
 * the iOS `LiveSuggesterLocationProvider._nearestStations` cap (50) —
 * the §4.6 Nearby state's full list; the idle Nearby-3 section takes the
 * first 3, so the host always hands the Suggester one ordered list it can
 * slice without re-resolving.
 */
const NEARBY_LIMIT = 50

/**
 * When the user is abroad (raw fix OUTSIDE the city's home region, so it was
 * remapped to the airport — London: the UK box and Heathrow), the Nearby
 * section must list ONLY the airport stations — the terminals — not the
 * merely-nearby non-airport stations the haversine also surfaces (Hatton
 * Cross, etc.). Cross-platform decision 2026-06-18: "abroad → only Heathrow
 * airport stations, the rest go". In-region fixes are returned unchanged
 * (real nearby). Station names are not localised, so the city's airport
 * name prefix cleanly selects the terminals (London "Heathrow": Terminals
 * 2 & 3 / 4 / 5; NYC "Terminal": the JFK and Newark AirTrain terminals).
 * Mirrors the iOS `LiveSuggesterLocationProvider._airportClampedIfAbroad`.
 */
function airportClampedIfAbroad(
  stations: readonly SuggesterStation[],
  rawFix: { latitude: number; longitude: number },
): readonly SuggesterStation[] {
  if (isInsideHomeRegion(rawFix)) return stations
  return stations.filter((station) => station.name.startsWith(CITY.airportStationPrefix))
}

/**
 * Rank the catalogue by real great-circle distance to `fix`, applying the
 * universal home-region → airport remap (§3.5) before the haversine — identical to
 * the map my-location path (`locationConsumerService`) and to the iOS
 * Suggester after its remap fix. Stations whose schema coordinate is
 * missing or the `(0, 0)` sentinel are dropped (mirrors the iOS
 * `_nearestStations` `coord.latitude != 0 || coord.longitude != 0`
 * guard). Result is ascending by distance, capped at {@link NEARBY_LIMIT},
 * each row carrying its real `distanceMeters`.
 */
function rankNearby(
  schema: SchemaData,
  catalogue: readonly SuggesterStation[],
  fix: { latitude: number; longitude: number },
): readonly SuggesterStation[] {
  // The remap (§3.5): outside the city's home region → resolve against
  // its airport (London: the UK box, Heathrow) so an out-of-region user gets
  // the city's stations
  // instead of a list "30,000 km away" (the user-reported bug). Inside
  // the region → identity.
  const appPoint = appLocation(fix)
  // Cluster-collapse to the same one-row-per-station catalogue the rest
  // of the Suggester uses, so the nearby ids join cleanly. Map each
  // catalogue station back to its schema record for the coordinate.
  const recordById = new Map<string, SchemaStation>()
  for (const record of schema.stations) recordById.set(record.id, record)

  const ranked: { station: SuggesterStation; distance: number }[] = []
  for (const station of catalogue) {
    const coord = recordById.get(station.id)?.coordinate
    if (coord === undefined) continue
    if (coord.latitude === 0 && coord.longitude === 0) continue
    ranked.push({ station, distance: haversineMeters(appPoint, coord) })
  }
  ranked.sort((a, b) => a.distance - b.distance)
  const nearby = ranked
    .slice(0, NEARBY_LIMIT)
    .map(({ station, distance }) => ({ ...station, distanceMeters: distance }))
  // Abroad clamp (§3.5): raw fix outside the region → keep ONLY the
  // airport terminals; in-region → real nearby unchanged. Keyed on the RAW
  // `fix` (pre-remap), not `appPoint` (which is always inside the region).
  return airportClampedIfAbroad(nearby, fix)
}

/**
 * Resolve the browser geolocation permission and (on grant) a real
 * distance-ranked nearby station list — the host bridge Suggester calls
 * for the §14 Location flow.
 *
 * Permission handling: `navigator.geolocation.getCurrentPosition`
 * surfaces the browser permission prompt; a `PERMISSION_DENIED` error
 * maps to `denied` so Suggester shows the "Location Access Required"
 * alert. Any other failure (timeout / position-unavailable) resolves to
 * `granted` with no stations so the picker stays in `idle` (Popular
 * backfills) rather than falsely claiming denial.
 *
 * Nearby ranking is REAL (TASK-geolocation-fix): `public/map-v2/schema.json`
 * carries a geographic `coordinate` for every station, so the granted fix
 * is ranked by great-circle distance via {@link rankNearby} — the
 * universal home-region → airport remap applied first, ascending by distance, real
 * `distanceMeters`. Mirrors the iOS Suggester (post-remap canon) and the
 * map my-location path. Popular is the fallback ONLY for denied /
 * unavailable, never the granted path.
 */
export function resolveNearby(
  schema: SchemaData | null,
  catalogue: readonly SuggesterStation[],
): () => Promise<NearbyResolution> {
  return () =>
    new Promise<NearbyResolution>((resolve) => {
      if (
        schema === null ||
        typeof navigator === 'undefined' ||
        navigator.geolocation === undefined
      ) {
        // No schema or no geolocation API — treat as denied so the user
        // gets the actionable "Location Access Required" alert rather
        // than a silent dead-end.
        resolve({ permission: 'denied' })
        return
      }
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const stations = rankNearby(schema, catalogue, {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          })
          resolve({ permission: 'granted', stations })
        },
        (error) => {
          resolve({
            permission: error.code === error.PERMISSION_DENIED ? 'denied' : 'granted',
          })
        },
        { timeout: 10_000 },
      )
    })
}

/**
 * Mount-time geolocation permission probe (#38, spec §14) — the host
 * bridge Suggester runs once on open to decide the "use my location"
 * banner visibility WITHOUT prompting.
 *
 * Reads `navigator.permissions.query({ name: 'geolocation' })`, which
 * reports the standing permission (`granted` / `prompt` / `denied`)
 * without surfacing the browser's permission dialog. On `granted` the
 * Suggester hides the banner and auto-resolves Nearby; on `prompt` /
 * `denied` the banner stays so the user can act.
 *
 * Falls back to `prompt` when the Permissions API is unavailable (older
 * Safari) — the safe default keeps the banner visible and the tap path
 * intact. Mirrors the iOS `permissionStatus()` probe.
 */
export async function probeLocationPermission(): Promise<LocationPermission> {
  if (
    typeof navigator === 'undefined' ||
    navigator.permissions === undefined ||
    typeof navigator.permissions.query !== 'function'
  ) {
    return 'prompt'
  }
  try {
    const status = await navigator.permissions.query({ name: 'geolocation' })
    // `PermissionState` is `'granted' | 'denied' | 'prompt'`, which maps
    // 1:1 onto `LocationPermission`.
    return status.state
  } catch {
    // A query rejection (unsupported name, locked-down context) is
    // treated as "not yet determined" — keep the banner, keep the tap.
    return 'prompt'
  }
}
